import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { publicApi } from "@/lib/api";

// Subtle branch accent colors (kept tonal, never gimmicky)
const BRANCH_ACCENT = {
  "U.S. Army": "bg-amber-50 text-amber-900 border-amber-200/60",
  "U.S. Navy": "bg-blue-50 text-blue-900 border-blue-200/60",
  "U.S. Marine Corps": "bg-red-50 text-red-900 border-red-200/60",
  "U.S. Air Force": "bg-sky-50 text-sky-900 border-sky-200/60",
  "U.S. Coast Guard": "bg-orange-50 text-orange-900 border-orange-200/60",
  "U.S. Space Force": "bg-slate-100 text-slate-900 border-slate-200/60",
};

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    publicApi.get("/public/reviews").then(({ data }) => setReviews(data || [])).catch(() => {});
  }, []);

  const visible = showAll ? reviews : reviews.slice(0, 9);

  return (
    <section id="reviews" className="section bg-[#F5F5F7]">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.6 }} className="max-w-2xl"
        >
          <p className="text-[11px] tracking-[0.22em] uppercase text-slate-500">From those we've served</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A1930] mt-3 leading-[1.05]">
            A wall of Veterans we've helped.
          </h2>
          <div className="mt-5 flex items-center gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-1 text-amber-500">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-amber-500" strokeWidth={0} />
              ))}
            </div>
            <span><span className="text-[#0A1930] font-medium">5.0</span> average · {reviews.length} reviews</span>
          </div>
        </motion.div>

        {/* Masonry wall via CSS columns */}
        <div
          className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]"
          data-testid="reviews-wall"
        >
          {visible.map((r, i) => (
            <ReviewCard r={r} key={r.review_id || i} i={i} />
          ))}
        </div>

        {reviews.length > 9 && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => setShowAll((v) => !v)}
              className="rounded-full border border-slate-300 bg-white text-[#0A1930] px-6 py-2.5 text-sm hover:bg-slate-50 transition-colors"
              data-testid="reviews-toggle-all"
            >
              {showAll ? "Show fewer reviews" : `Show all ${reviews.length} reviews`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function ReviewCard({ r, i }) {
  const accent = BRANCH_ACCENT[r.branch] || "bg-slate-50 text-slate-900 border-slate-200/60";
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.4) }}
      className="break-inside-avoid mb-5 bg-white border border-slate-200 rounded-3xl p-6 hover:-translate-y-0.5 hover:shadow-[0_20px_50px_-30px_rgba(10,25,48,0.25)] transition-all"
      data-testid={`review-card-${i}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-amber-500">
          {Array.from({ length: r.rating || 5 }).map((_, j) => (
            <Star key={j} className="w-3.5 h-3.5 fill-amber-500" strokeWidth={0} />
          ))}
        </div>
        <Quote className="w-4 h-4 text-slate-300" strokeWidth={1.5} />
      </div>

      <p className="mt-4 text-[15px] leading-relaxed text-[#1D1D1F]">"{r.body}"</p>

      <div className="mt-6 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#0A1930] text-white grid place-items-center text-[11px] font-medium tracking-wide">
          {(r.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#0A1930] truncate">{r.name}</p>
          <span className={`inline-flex items-center text-[10px] tracking-[0.14em] uppercase mt-0.5 rounded-full border px-2 py-0.5 ${accent}`}>
            {r.branch}
          </span>
        </div>
      </div>
    </motion.article>
  );
}
